import theme from "../../themes/default";
import React from "react";
import {
  Card,
  Title,
  Important,
  Description,
  TipsCard,
  TipRow,
  TipText,
  FormSection,
  TipsAccordionButton,
  TipsAccordionContent,
  TipsAccordionArrow,
  Wrapper,
} from "./CompanyEmailStep.elements";
import { ReactComponent as FastIcon } from "./../../assets/icons/fast.svg";
import { ReactComponent as BankIcon } from "./../../assets/icons/bank.svg";
import { ReactComponent as LawIcon } from "./../../assets/icons/law.svg";
import { ReactComponent as LockIcon } from "./../../assets/icons/lock.svg";
import { ReactComponent as SecureIcon } from "./../../assets/icons/secure.svg";
import ArrowIcon from "./ArrowIcon";
import { useTranslation } from "react-i18next";
import TextInput, { TextInputType } from "../TextInput/TextInput";
import CountrySelector, {
  CountryOption,
} from "../CountrySelector/CountrySelector";
import { useForm } from "react-hook-form";
import { ButtonPrimary } from "../Button/Button";
import { useDispatch, useSelector } from "react-redux";
import { countriesActions, countriesSelectors } from "../../redux/countries";
import {
  DispatchType,
  registrationActions,
  registrationSelectors,
  uiSelectors,
} from "../../redux";
import { showModal } from "../../redux/modal";
import ErrorModalContent from "../../components/ErrorModalContent";
import { Spinner } from "../Spinner/Spinner";

export type CompanyEmailStepProps = {
  email: string;
  setEmail: (email: string) => void;
  country: string;
  setCountry: (country: string) => void;
  onNext: () => void;
};

export default function CompanyEmailStep(props: CompanyEmailStepProps) {
  const { email, setEmail, country, setCountry, onNext } = props;
  const { t } = useTranslation();
  const [tipsOpen, setTipsOpen] = React.useState(false);
  const dispatch = useDispatch<DispatchType>();
  const countries = useSelector(countriesSelectors.getCountries);
  const countriesLoading = useSelector(countriesSelectors.getCountriesLoading);
  const language = useSelector(uiSelectors.getLanguage);
  const registrationLoading = useSelector(registrationSelectors.getLoading);

  React.useEffect(() => {
    if (countries.length === 0 && !countriesLoading) {
      dispatch(countriesActions.fetchCountries())
        .unwrap()
        .catch((error) => {
          dispatch(
            showModal({
              title: t("common.error.title"),
              content: <ErrorModalContent message={error} />,
            })
          );
        });
    }
  }, [dispatch, countries.length, countriesLoading]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
  } = useForm<{ email: string; country: string }>({
    defaultValues: { email, country },
  });

  React.useEffect(() => {
    setValue("email", email);
  }, [email, setValue]);

  React.useEffect(() => {
    setValue("country", country);
  }, [country, setValue]);

  const watchedEmail = watch("email", email);
  const watchedCountry = watch("country", country);
  const emailRegex = /^\S+@\S+\.\S+$/;
  const isEmailValid = emailRegex.test(watchedEmail);
  const isCountryValid = watchedCountry.length > 0;

  async function onSubmit() {
    if (isEmailValid && isCountryValid) {
      try {
        await dispatch(
          registrationActions.generateRegistrationToken({
            language: language.toLowerCase(),
            country: watchedCountry,
            email: watchedEmail,
            isCompany: true,
            captchaToken: "",
            promoNotifications: false,
          })
        )
          .unwrap()
          .then(() => {
            onNext();
          })
          .catch((error) => {
            dispatch(
              showModal({
                title: t("common.error.title"),
                content: <ErrorModalContent message={error} />,
              })
            );
          });
      } catch (error) {
        console.error("Registration token generation failed:", error);
      }
    }
  }

  const tips = [
    {
      icon: LawIcon,
      text: t("registration.businessStep.tips.0"),
    },
    {
      icon: SecureIcon,
      text: t("registration.businessStep.tips.1"),
    },
    {
      icon: LockIcon,
      text: t("registration.businessStep.tips.2"),
    },
    {
      icon: FastIcon,
      text: t("registration.businessStep.tips.3"),
    },
    {
      icon: BankIcon,
      text: t("registration.businessStep.tips.4"),
    },
  ];

  const countryOptions: CountryOption[] = countries
    .map((c) => ({
      code: c.alpha3Code,
      name: t(`countries.countryName_${c.alpha3Code.toLowerCase()}`),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Wrapper>
      <Card>
        <Title>{t("registration.businessStep.title")}</Title>
        <Important>{t("registration.businessStep.important")}</Important>
        <Description>{t("registration.businessStep.description")}</Description>
        <TipsAccordionButton onClick={() => setTipsOpen((o) => !o)}>
          {t("registration.businessStep.tipsLabel")}
          <TipsAccordionArrow open={tipsOpen}>
            <ArrowIcon color={theme.colors.primary300} />
          </TipsAccordionArrow>
        </TipsAccordionButton>
        <TipsAccordionContent open={tipsOpen}>
          <TipsCard>
            {tips.map(({ icon: Icon, text }, idx) => (
              <TipRow key={idx}>
                <Icon style={{ width: 40, height: 40, minWidth: 40 }} />
                <TipText>{text}</TipText>
              </TipRow>
            ))}
          </TipsCard>
        </TipsAccordionContent>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <FormSection>
            <CountrySelector
              label={t("registration.businessStep.countryLabel")}
              value={watchedCountry}
              onChange={(code) => {
                setValue("country", code, { shouldValidate: true });
                setCountry(code);
                clearErrors("country");
              }}
              options={countryOptions}
              placeholder={t("registration.businessStep.countryPlaceholder")}
              loading={countriesLoading}
            />
            <TextInput
              label={t("registration.businessStep.emailLabel")}
              register={register}
              name="email"
              type={TextInputType.EMAIL}
              value={watchedEmail}
              onChange={setEmail}
              placeholder={t("registration.businessStep.emailPlaceholder")}
            />
          </FormSection>
          <ButtonPrimary
            disabled={!isEmailValid || !isCountryValid || registrationLoading}
            onClick={onSubmit}
          >
            {registrationLoading && <Spinner size="small" />}
            {t("registration.businessStep.send")}
          </ButtonPrimary>
        </form>
      </Card>
    </Wrapper>
  );
}
